import { get } from 'lodash';

import { affiliateIds } from 'constants/affiliates';
import networkAdapter from '../adapters/network';
import { UnitsOfMeasureTemplateNames } from '../analytics/types';

const _sendBeginSendScriptsToAffiliate = ({
  url,
  requestParams = {},
  userId,
}: {
  url: string;
  requestParams?: object;
  userId: string;
}) => {
  return new Promise<string>((resolve, reject) => {
    const legacyOptions = {
      url: url,
      userId,
      method: 'POST',
      body: JSON.stringify(requestParams),
    };

    return networkAdapter({
      legacyOptions,
    })
    .then((response: Response) => {
      resolve(get(response, 'jobUUID'));
    })
    .catch((err: Error) => {
      reject(err);
    });
  });
};

// Fixes https://climate.jira.com/browse/SG-490 while Mycroft adds support for Canadian units of measure.
const getUnitsForQuerystring = (unitTemplate: UnitsOfMeasureTemplateNames) =>
  unitTemplate === UnitsOfMeasureTemplateNames.CANADA ? UnitsOfMeasureTemplateNames.IMPERIAL : unitTemplate;

export const sendBeginBulkSendScriptsToAffiliate = ({
  affiliateId,
  fieldIds,
  organizationId,
  preferredDeviceId,
  primaryOnly,
  scriptType,
  season,
  userId,
  unitTemplate,
}: {
  affiliateId: string;
  fieldIds: number[];
  organizationId: string;
  preferredDeviceId: string;
  primaryOnly: boolean;
  scriptType: string;
  season: string;
  userId: string;
  unitTemplate: UnitsOfMeasureTemplateNames;
}) => {
  const querystring = getUnitsForQuerystring(unitTemplate);
  return _sendBeginSendScriptsToAffiliate({
    url: `/api/mycroft/v2/prescriptions/push/bulk?primary-only=${primaryOnly}&units=${querystring}`,
    userId,
    requestParams: {
      'activity-prefix': scriptType,
      'field-ids': fieldIds,
      'monitor-format': preferredDeviceId,
      'organization-id': organizationId,
      partner: affiliateId,
      season: season,
    },
  });
};

export const sendBeginSingleSendScriptToAffiliate = ({
  affiliateId,
  fieldId,
  organizationId,
  scriptId,
  userId,
  unitTemplate,
}: {
  affiliateId: string;
  fieldId: string;
  organizationId: string;
  scriptId: string;
  userId: string;
  unitTemplate: UnitsOfMeasureTemplateNames;
}) => {
  const units = getUnitsForQuerystring(unitTemplate);
  const querystring = `format=shapefile&organization-id=${organizationId}&units=${units}`;
  return _sendBeginSendScriptsToAffiliate({
    url: `/api/mycroft/v2/prescriptions/push/${affiliateId}/${fieldId}/${scriptId}?${querystring}`,
    userId,
  });
};

export const sendConnectToAffiliate = ({ affiliateId, userId }: { affiliateId: string;  userId: string }) => {
  switch (affiliateId) {
    case affiliateIds.MYJD:
      const legacyOptions = {
        url: `/api/deere/link`,
        userId,
        method: 'POST',
        body: JSON.stringify({
          callback_url: `${window.location.origin}/api/deere/callback?url=${encodeURIComponent(window.location.href)}`,
        }),
      };

      return networkAdapter({
        legacyOptions,
      });
    default:
      return Promise.resolve();
  }
};

export const sendBeginWorkPlanToJohnDeere = ({
  fieldId,
  jdFieldId,
  organizationId,
  scriptId,
  unitTemplate,
  userId,
}: {
  fieldId: number;
  jdFieldId: string;
  organizationId: string;
  scriptId: string;
  unitTemplate: UnitsOfMeasureTemplateNames;
  userId: string;
}) => {
  const units = getUnitsForQuerystring(unitTemplate);
  const querystring = `organization-id=${organizationId}&units=${units}&jdFieldId=${jdFieldId}`;
  return _sendBeginSendScriptsToAffiliate({
    url: `/api/mycroft/v2/prescriptions/push/deere/${fieldId}/${scriptId}?${querystring}`,
    userId,
  });
};

export const unitTestPrivate = {
  _sendBeginSendScriptsToAffiliate,
};
